import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { Button, notification } from 'antd';
import * as API from 'aws-amplify/api';
import Context from '../../context';
import { CAPACITY_SUBSCRIPTION_SCHEMA } from '../../schemas/capacitySubscriptions';
import { formatPrice, formatNumber } from '../../utils/i18n';
import { startOfGasYear } from '../../utils/gasday';
import ModalWrapper, { modalConfirmClose, modalConfirmSave } from '../ModalWrapper';
import CapacitySubscriptionForm from './CapacitySubscriptionForm';
import { createErrorMessage } from '../../context/globalStateHelper';

import './index.less';

const CapacitySubscription = ({ onClose }) => {
  const { t } = useTranslation();
  const {
    selectedMarketPartyId,
    capacitySubscriptionPrices,
    updateCapacityPrices,
  } = useContext(Context);

  useEffect(() => {
    if (!capacitySubscriptionPrices) updateCapacityPrices();
  }, [capacitySubscriptionPrices, updateCapacityPrices]);

  const currentYear = moment.utc().year();
  const availableYears = useMemo(
    () => Array.from({ length: 5 }, (_, index) => currentYear + index),
    [currentYear],
  );
  const initialYear = currentYear;
  const [selectedYear, setSelectedYear] = useState(initialYear);
  const periodStart = useMemo(
    () => startOfGasYear(String(selectedYear)),
    [selectedYear],
  );
  const periodEnd = useMemo(
    () => startOfGasYear(String(selectedYear + 1)).subtract(1, 'day'),
    [selectedYear],
  );

  const [subscribedCapacity, setSubscribedCapacity] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [hasValidationError, setHasValidationError] = useState(false);
  const getEstimatedCosts = useCallback(() => {
    if (!subscribedCapacity || subscribedCapacity <= 0) {
      return null;
    }

    // Find the most recent price or fallback to 0
    const mostRecentPrice = capacitySubscriptionPrices && capacitySubscriptionPrices.length > 0
      ? capacitySubscriptionPrices.reduce((latest, current) => (
        current.timestamp > latest.timestamp ? current : latest
      ))
      : null;
    const pricePerMWh = mostRecentPrice ? Number(mostRecentPrice.price) : 0;

    const annual = subscribedCapacity * pricePerMWh;
    const monthly = annual / 12;
    return {
      annual,
      monthly,
    };
  }, [subscribedCapacity, capacitySubscriptionPrices]);

  const getPayload = useCallback(() => {
    const subscribedCapacityValue = subscribedCapacity != null
      ? Number(subscribedCapacity) * 1000
      : null;
    return {
      startDate: periodStart.clone().utc().format('YYYY-MM-DDTHH:mm:ss[Z]'),
      endDate: periodEnd.clone().utc().format('YYYY-MM-DDTHH:mm:ss[Z]'),
      subscribedCapacity: subscribedCapacityValue,
    };
  }, [periodEnd, periodStart, subscribedCapacity]);

  const handleCapacityChange = useCallback((value) => {
    setSubscribedCapacity(value);
    setHasChanges(true);
    setHasValidationError(!value || value <= 0);
  }, []);

  const handleYearChange = useCallback((value) => {
    setSelectedYear(value);
    setHasChanges(true);
  }, []);

  const isEditWindowOpen = useMemo(() => {
    const cutoffYear = selectedYear - 1;
    const cutoffDate = moment(`${cutoffYear}-12-15T23:59:59Z`);
    return moment.utc().isSameOrBefore(cutoffDate);
  }, [selectedYear]);

  const isSaveDisabled = isSaving || !subscribedCapacity || subscribedCapacity <= 0;

  const handleClose = useCallback(() => (
    hasChanges
      ? modalConfirmClose(t, () => onClose())
      : onClose()
  ), [hasChanges, onClose, t]);

  const renderConfirmContent = useCallback(() => {
    const estimatedCosts = getEstimatedCosts();
    const formattedCapacity = subscribedCapacity === null
      ? '--'
      : formatNumber(subscribedCapacity, 0, 3);

    return (
      <div className="capacitysubscription__confirm">
        <p>{t('capacitySubscription.dialog.actionMessage')}</p>
        <p>
          {t('capacitySubscription.dialog.capacityValue', {
            value: formattedCapacity,
          })}
        </p>
        {estimatedCosts !== null && (
          <p>
            {t('capacitySubscription.cost.format', {
              annual: formatPrice(estimatedCosts.annual),
              monthly: formatPrice(estimatedCosts.monthly),
            })}
          </p>
        )}
      </div>
    );
  }, [getEstimatedCosts, subscribedCapacity, t]);

  const handleSave = useCallback(async () => {
    const payload = getPayload();
    const { error } = CAPACITY_SUBSCRIPTION_SCHEMA.validate(payload);

    if (error) {
      setHasValidationError(true);
      notification.error({
        className: 'notification-error',
        message: t('common.notifications.incorrectInput'),
        description: error.toString(),
      });
      return;
    }

    try {
      setIsSaving(true);
      const { body } = await API.post({
        apiName: 'FINTSO',
        path: `/marketparties/${selectedMarketPartyId}/capacity-subscriptions`,
        options: { body: payload },
      }).response;
      const response = await body.json();
      notification.success({
        message: t('capacitySubscription.message.createdSuccessfully'),
        description: '',
        placement: 'bottomRight',
      });
      onClose(response);
    } catch (errorSaving) {
      notification.error({
        className: 'notification-error',
        message: t('common.notifications.failedToSave'),
        description: await createErrorMessage(errorSaving),
      });
      setIsSaving(false);
    }
  }, [
    getPayload,
    onClose,
    selectedMarketPartyId,
    t,
  ]);

  const handleSaveRequest = useCallback(() => {
    modalConfirmSave(
      t,
      handleSave,
      null,
      null,
      renderConfirmContent(),
    );
  }, [handleSave, renderConfirmContent, t]);

  const estimatedCosts = getEstimatedCosts();
  const estimatedCostText = estimatedCosts !== null ? t('capacitySubscription.cost.format', {
    annual: formatPrice(estimatedCosts.annual),
    monthly: formatPrice(estimatedCosts.monthly),
  }) : null;

  return (
    <ModalWrapper
      modalClassName="capacitysubscription__modal"
      title={t('capacitySubscription.form.title.newCapacitySubscription')}
      handleClose={handleClose}
      actionButtons={[
        <Button
          className="capacitysubscription__modal__cancel-button"
          key="cancel"
          onClick={handleClose}
        >
          {t('common.button.cancel')}
        </Button>,
        <Button
          className="capacitysubscription__modal__save-button"
          disabled={isSaveDisabled}
          key="save"
          loading={isSaving}
          type="primary"
          onClick={handleSaveRequest}
        >
          {t('common.button.saveAndClose')}
        </Button>,
      ]}
      width="40rem"
    >
      <CapacitySubscriptionForm
        period={{
          startDate: periodStart.toISOString(),
          endDate: periodEnd.toISOString(),
        }}
        subscribedCapacity={subscribedCapacity}
        estimatedCostText={estimatedCostText}
        selectedYear={selectedYear}
        availableYears={availableYears}
        onYearChange={handleYearChange}
        isEditWindowOpen={isEditWindowOpen}
        onCapacityChange={handleCapacityChange}
        hasValidationError={hasValidationError}
      >
        <div className="capacitysubscription__modal__disclaimer">
          <p className="capacitysubscription__modal__disclaimer__label">
            {`${t('capacitySubscription.form.label.disclaimer')}:`}
          </p>
          <p>{t('capacitySubscription.form.info.line1')}</p>
          <p>{t('capacitySubscription.form.info.line2')}</p>
        </div>
      </CapacitySubscriptionForm>
    </ModalWrapper>
  );
};


CapacitySubscription.displayName = 'CapacitySubscription';

CapacitySubscription.propTypes = {
  onClose: PropTypes.func.isRequired,
};

export default CapacitySubscription;
export {
  CapacitySubscription as PureComponent,
};
